import { useState, useEffect } from "react";
import {
  Search,
  Edit,
  Trash2,
  Eye,
  X,
  AlertTriangle,
  MessageSquare,
  Plus,
  FolderPlus,
} from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/constants";

// Interface pour les sujets du forum
interface Author {
  id: string;
  firstName: string;
  lastName: string;
}

interface ForumTopic {
  id: string;
  title: string;
  content: string;
  authorId: string;
  author?: Author;
  createdAt: string;
  updatedAt: string;
  commentsCount: number;
  isLocked: boolean;
  isPinned: boolean;
}

// Interface pour les catégories du forum
interface ForumCategory {
  id: string;
  name: string;
  description: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  topicsCount: number;
  topics?: ForumTopic[];
}

// Interface pour le formulaire de modification de sujet
interface UpdateTopicForm {
  title: string;
  content: string;
  isLocked: boolean;
  isPinned: boolean;
}

// Interface pour le formulaire de modification de catégorie
interface UpdateCategoryForm {
  name: string;
  description: string;
  order: number;
  isActive: boolean;
}

// Interface pour le formulaire de création de sujet
interface CreateTopicForm {
  title: string;
  content: string;
  isPinned: boolean;
  categoryId: string;
}

// Interface pour le formulaire de création de catégorie
interface CreateCategoryForm {
  name: string;
  description: string;
  order: number;
  isActive: boolean;
}

const AdminForum = () => {
  // États pour les catégories
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // États pour les sujets par catégorie
  const [selectedCategory, setSelectedCategory] =
    useState<ForumCategory | null>(null);
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState("");
  const [isViewCategoryTopicsModalOpen, setIsViewCategoryTopicsModalOpen] =
    useState(false);

  // États pour la création de catégorie
  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] =
    useState(false);
  const [createCategoryForm, setCreateCategoryForm] =
    useState<CreateCategoryForm>({
      name: "",
      description: "",
      order: 1,
      isActive: true,
    });
  const [createCategoryLoading, setCreateCategoryLoading] = useState(false);
  const [createCategoryError, setCreateCategoryError] = useState("");
  const [createCategorySuccess, setCreateCategorySuccess] = useState(false);

  // États pour la modification de catégorie
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] =
    useState<ForumCategory | null>(null);
  const [editCategoryForm, setEditCategoryForm] = useState<UpdateCategoryForm>({
    name: "",
    description: "",
    order: 1,
    isActive: true,
  });
  const [editCategoryLoading, setEditCategoryLoading] = useState(false);
  const [editCategoryError, setEditCategoryError] = useState("");
  const [editCategorySuccess, setEditCategorySuccess] = useState(false);

  // États pour la suppression de catégorie
  const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] =
    useState(false);
  const [categoryToDelete, setCategoryToDelete] =
    useState<ForumCategory | null>(null);
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);
  const [deleteCategoryError, setDeleteCategoryError] = useState("");
  const [deleteCategorySuccess, setDeleteCategorySuccess] = useState(false);

  // États pour la création de sujet
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTopicForm>({
    title: "",
    content: "",
    isPinned: false,
    categoryId: "1", // Valeur par défaut pour la catégorie
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);

  // États pour l'édition de sujet
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<ForumTopic | null>(null);
  const [editForm, setEditForm] = useState<UpdateTopicForm>({
    title: "",
    content: "",
    isLocked: false,
    isPinned: false,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState(false);

  // États pour la suppression de sujet
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<ForumTopic | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // États pour la visualisation d'un sujet
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [topicToView, setTopicToView] = useState<ForumTopic | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Récupérer les catégories
      const response = await fetch(`${API_ENDPOINTS.FORUM.GET_CATEGORIES}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Raw API response for categories:", data);

      // Trier les catégories par ordre
      const categoriesWithCounts = await Promise.all(
        [...data]
          .sort((a, b) => a.order - b.order)
          .map(async (category) => {
            try {
              // Récupérer le comptage des sujets depuis l'API
              const countResponse = await fetch(
                `${API_ENDPOINTS.FORUM.GET_TOPICS}?categoryId=${category.id}`,
                {
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                  },
                }
              );

              if (!countResponse.ok) {
                console.error(
                  `Erreur lors de la récupération du comptage pour la catégorie ${category.id}`
                );
                return { ...category, topicsCount: 0 };
              }

              // Extraire le comptage de la réponse
              const countData = await countResponse.json();
              console.log(`Response for category ${category.id}:`, countData);

              // Utiliser le champ "count" fourni par l'API
              const count = countData.count || 0;
              console.log(`Count for category ${category.id}:`, count);

              return { ...category, topicsCount: count };
            } catch (err) {
              console.error(
                `Erreur lors de la récupération du comptage pour la catégorie ${category.id}:`,
                err
              );
              return { ...category, topicsCount: 0 };
            }
          })
      );

      console.log("Categories with topic counts:", categoriesWithCounts);
      setCategories(categoriesWithCounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des catégories:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryTopics = async (categoryId: string) => {
    setTopicsLoading(true);
    setTopicsError("");
    console.log(`Chargement des sujets pour la catégorie ${categoryId}`);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser l'endpoint réel pour récupérer les sujets par catégorie
      // Ajouter les paramètres pour inclure le contenu et l'auteur
      const response = await fetch(
        `${API_ENDPOINTS.FORUM.GET_TOPICS}?categoryId=${categoryId}&include=author,content`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Raw API response:", data); // Debug log

      // Vérifier si la réponse a la structure attendue
      const topicsArray = data.topics || [];
      console.log("Topics array:", topicsArray);

      // S'assurer que chaque sujet a le contenu et toutes les données nécessaires
      const processedTopics = topicsArray.map((topic: Partial<ForumTopic>) => {
        console.log("Processing topic:", topic);
        return {
          ...topic,
          authorId: topic.authorId || "unknown",
          author: topic.author || {
            id: "unknown",
            firstName: "Utilisateur",
            lastName: "Inconnu",
          },
          content: topic.content || "", // S'assurer que le contenu existe
          commentsCount: topic.commentsCount || 0,
          isLocked: topic.isLocked || false,
          isPinned: topic.isPinned || false,
          createdAt: topic.createdAt || new Date().toISOString(),
          updatedAt:
            topic.updatedAt || topic.createdAt || new Date().toISOString(),
        };
      });

      console.log("Processed topics:", processedTopics); // Debug log
      setTopics(processedTopics);
    } catch (err) {
      setTopicsError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de récupération des sujets:", err);
    } finally {
      setTopicsLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    formType: "create" | "edit" | "createCategory" | "editCategory"
  ) => {
    const { name, value } = e.target;
    if (formType === "create") {
      setCreateForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (formType === "edit") {
      setEditForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (formType === "createCategory") {
      setCreateCategoryForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (formType === "editCategory") {
      setEditCategoryForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    formType: "create" | "edit" | "createCategory" | "editCategory"
  ) => {
    const { name, checked } = e.target;
    if (formType === "create") {
      setCreateForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (formType === "edit") {
      setEditForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (formType === "createCategory") {
      setCreateCategoryForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (formType === "editCategory") {
      setEditCategoryForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    }
  };

  const handleEditCategoryButtonClick = (category: ForumCategory) => {
    setSelectedCategoryForEdit(category);
    setEditCategoryForm({
      name: category.name,
      description: category.description || "",
      order: category.order,
      isActive: category.isActive,
    });
    setIsEditCategoryModalOpen(true);
    setEditCategoryError("");
    setEditCategorySuccess(false);
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategoryForEdit) return;

    setEditCategoryLoading(true);
    setEditCategoryError("");
    setEditCategorySuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.FORUM.UPDATE_CATEGORY(selectedCategoryForEdit.id),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            name: editCategoryForm.name,
            description: editCategoryForm.description,
            order: editCategoryForm.order,
            isActive: editCategoryForm.isActive,
          }),
        }
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              "Erreur lors de la modification de la catégorie"
          );
        } else {
          const errorText = await response.text();
          throw new Error(
            errorText || `Erreur ${response.status}: ${response.statusText}`
          );
        }
      }

      // Mettre à jour la liste des catégories
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === selectedCategoryForEdit.id
            ? {
                ...category,
                name: editCategoryForm.name,
                description: editCategoryForm.description,
                order: editCategoryForm.order,
                isActive: editCategoryForm.isActive,
                updatedAt: new Date().toISOString(),
              }
            : category
        )
      );

      setEditCategorySuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsEditCategoryModalOpen(false);
        setEditCategorySuccess(false);
      }, 2000);
    } catch (err) {
      setEditCategoryError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de modification de la catégorie:", err);
    } finally {
      setEditCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    setDeleteCategoryLoading(true);
    setDeleteCategoryError("");
    setDeleteCategorySuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.FORUM.DELETE_CATEGORY(categoryToDelete.id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Erreur lors de la suppression de la catégorie"
          );
        } else {
          const errorText = await response.text();
          throw new Error(
            errorText || `Erreur ${response.status}: ${response.statusText}`
          );
        }
      }

      // Supprimer la catégorie de la liste
      setCategories((prevCategories) =>
        prevCategories.filter((category) => category.id !== categoryToDelete.id)
      );

      setDeleteCategorySuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsDeleteCategoryModalOpen(false);
        setDeleteCategorySuccess(false);
        setCategoryToDelete(null);
      }, 2000);
    } catch (err) {
      setDeleteCategoryError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de suppression de la catégorie:", err);
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  const handleViewCategoryTopics = (category: ForumCategory) => {
    console.log("Viewing topics for category:", category);
    setSelectedCategory(category);
    fetchCategoryTopics(category.id.toString());
    setIsViewCategoryTopicsModalOpen(true);
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    setCreateCategoryLoading(true);
    setCreateCategoryError("");
    setCreateCategorySuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser l'endpoint depuis les constantes
      const response = await fetch(API_ENDPOINTS.FORUM.CREATE_CATEGORY, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: createCategoryForm.name,
          description: createCategoryForm.description,
          order: createCategoryForm.order,
          isActive: createCategoryForm.isActive,
        }),
      });

      if (!response.ok) {
        // Vérifier le type de contenu de la réponse
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Erreur lors de la création de la catégorie"
          );
        } else {
          // Si ce n'est pas du JSON, utiliser le texte de réponse ou le statut
          const errorText = await response.text();
          throw new Error(
            errorText || `Erreur ${response.status}: ${response.statusText}`
          );
        }
      }

      // Vérifier si la réponse contient du contenu
      const contentType = response.headers.get("content-type");

      if (
        contentType &&
        contentType.includes("application/json") &&
        response.headers.get("content-length") !== "0"
      ) {
        const data = await response.json();

        // S'assurer que toutes les propriétés requises sont présentes
        const newCategory: ForumCategory = {
          id: data.id,
          name: data.name,
          description: data.description,
          order: data.order,
          isActive: data.isActive,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          topicsCount: 0,
        };

        // Ajouter la nouvelle catégorie à la liste
        setCategories((prevCategories) => [...prevCategories, newCategory]);
      }

      setCreateCategorySuccess(true);

      // Réinitialiser le formulaire
      setCreateCategoryForm({
        name: "",
        description: "",
        order: 1,
        isActive: true,
      });

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateCategoryModalOpen(false);
        setCreateCategorySuccess(false);
      }, 2000);
    } catch (err) {
      setCreateCategoryError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création de catégorie:", err);
    } finally {
      setCreateCategoryLoading(false);
    }
  };

  const handleCreateTopic = async (e: React.FormEvent) => {
    e.preventDefault();

    // Valider que les champs requis sont remplis
    if (!createForm.title.trim()) {
      setCreateError("Le titre est requis");
      return;
    }

    if (!createForm.content.trim()) {
      setCreateError("Le contenu est requis");
      return;
    }

    if (!createForm.categoryId) {
      setCreateError("Veuillez sélectionner une catégorie");
      return;
    }

    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // S'assurer que le contenu n'est pas vide
      const contentToSend =
        createForm.content.trim() || "Pas de contenu disponible";

      // Utiliser l'endpoint depuis les constantes
      const response = await fetch(API_ENDPOINTS.FORUM.CREATE_TOPIC, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          title: createForm.title.trim(),
          content: contentToSend,
          categoryId: createForm.categoryId,
          isPinned: createForm.isPinned,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la création du sujet"
        );
      }

      const newTopic = await response.json();
      console.log("New topic response:", newTopic); // Debug log

      // Vérifier la structure de la réponse et ajuster si nécessaire
      const topicData = newTopic.topic || newTopic;

      // Ajouter le nouveau sujet à la liste avec des valeurs par défaut si nécessaire
      setTopics((prevTopics) => [
        {
          ...topicData,
          id: topicData.id,
          title: topicData.title,
          content: topicData.content,
          authorId: topicData.authorId || "currentUser",
          author: topicData.author || {
            id: "unknown",
            firstName: "Utilisateur",
            lastName: "Inconnu",
          },
          createdAt: topicData.createdAt || new Date().toISOString(),
          updatedAt:
            topicData.updatedAt ||
            topicData.createdAt ||
            new Date().toISOString(),
          commentsCount: topicData.commentsCount || 0,
          isLocked: topicData.isLocked || false,
          isPinned: topicData.isPinned || createForm.isPinned,
        },
        ...prevTopics,
      ]);

      // Mettre à jour le compteur de sujets dans la catégorie
      if (selectedCategory && createForm.categoryId === selectedCategory.id) {
        setCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === createForm.categoryId
              ? {
                  ...category,
                  topicsCount: (category.topicsCount || 0) + 1,
                }
              : category
          )
        );
      }

      setCreateSuccess(true);

      // Réinitialiser le formulaire
      setCreateForm({
        title: "",
        content: "",
        isPinned: false,
        categoryId: createForm.categoryId, // Conserver la catégorie sélectionnée
      });

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création du sujet:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditButtonClick = (topic: ForumTopic) => {
    setSelectedTopic(topic);
    setEditForm({
      title: topic.title,
      content: topic.content,
      isLocked: topic.isLocked,
      isPinned: topic.isPinned,
    });
    setIsEditModalOpen(true);
    setEditError("");
    setEditSuccess(false);
  };

  const handleUpdateTopic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTopic) return;

    // Valider que les champs requis sont remplis
    if (!editForm.title.trim()) {
      setEditError("Le titre est requis");
      return;
    }

    if (!editForm.content.trim()) {
      setEditError("Le contenu est requis");
      return;
    }

    setEditLoading(true);
    setEditError("");
    setEditSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // S'assurer que le contenu n'est pas vide
      const contentToSend =
        editForm.content.trim() || "Pas de contenu disponible";

      const response = await fetch(
        API_ENDPOINTS.FORUM.UPDATE_TOPIC(selectedTopic.id),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            title: editForm.title.trim(),
            content: contentToSend,
            isLocked: editForm.isLocked,
            isPinned: editForm.isPinned,
          }),
        }
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Erreur lors de la modification du sujet"
          );
        } else {
          const errorText = await response.text();
          throw new Error(
            errorText || `Erreur ${response.status}: ${response.statusText}`
          );
        }
      }

      // Mettre à jour le sujet dans la liste
      setTopics((prevTopics) =>
        prevTopics.map((topic) =>
          topic.id === selectedTopic.id
            ? {
                ...topic,
                title: editForm.title.trim(),
                content: editForm.content.trim(),
                isLocked: editForm.isLocked,
                isPinned: editForm.isPinned,
                updatedAt: new Date().toISOString(),
                authorId: topic.authorId,
                commentsCount: topic.commentsCount,
              }
            : topic
        )
      );

      setEditSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsEditModalOpen(false);
        setEditSuccess(false);
      }, 2000);
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de modification du sujet:", err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!topicToDelete || !selectedCategory) return;

    setDeleteLoading(true);
    setDeleteError("");
    setDeleteSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        API_ENDPOINTS.FORUM.DELETE_TOPIC(topicToDelete.id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Erreur lors de la suppression du sujet"
          );
        } else {
          const errorText = await response.text();
          throw new Error(
            errorText || `Erreur ${response.status}: ${response.statusText}`
          );
        }
      }

      // Supprimer le sujet de la liste
      setTopics((prevTopics) =>
        prevTopics.filter((topic) => topic.id !== topicToDelete.id)
      );

      // Mettre à jour le compteur de sujets dans la catégorie
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === selectedCategory.id
            ? {
                ...category,
                topicsCount: Math.max((category.topicsCount || 0) - 1, 0),
              }
            : category
        )
      );

      setDeleteSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setDeleteSuccess(false);
        setTopicToDelete(null);
      }, 2000);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de suppression du sujet:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Forum</h1>
      <p className="mt-4">Gérez le forum de la plateforme.</p>
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Catégories du forum</h2>
          <div className="flex gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={18}
              />
            </div>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              onClick={() => setIsCreateCategoryModalOpen(true)}
            >
              <FolderPlus size={18} />
              <span>Créer une catégorie</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="ml-2">Chargement des catégories...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
            {error}
          </div>
        ) : categories.length === 0 ? (
          <p className="text-center py-8 text-gray-500">
            Aucune catégorie disponible pour le moment.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Nom
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Sujets
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Statut
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories
                  .filter(
                    (category) =>
                      category.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      (category.description &&
                        category.description
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()))
                  )
                  .sort((a, b) => a.order - b.order)
                  .map((category, index) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {category.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-500 truncate max-w-xs">
                          {category.description || "—"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.topicsCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {category.isActive ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <button
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                            onClick={() =>
                              handleEditCategoryButtonClick(category)
                            }
                            title="Modifier"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() => {
                              setCategoryToDelete(category);
                              setIsDeleteCategoryModalOpen(true);
                            }}
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100 transition-colors"
                            onClick={() => handleViewCategoryTopics(category)}
                            title="Voir les sujets"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de création de catégorie */}
      {isCreateCategoryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsCreateCategoryModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">
              Créer une nouvelle catégorie
            </h2>

            {createCategorySuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Catégorie créée avec succès!</p>
              </div>
            )}

            {createCategoryError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createCategoryError}</p>
              </div>
            )}

            <form onSubmit={handleCreateCategory}>
              <div className="mb-4">
                <label htmlFor="create-category-name" className="block text-gray-700 text-sm font-bold mb-2">
                  Nom de la catégorie
                </label>
                <input
                  id="create-category-name"
                  type="text"
                  name="name"
                  value={createCategoryForm.name}
                  onChange={(e) => handleInputChange(e, "createCategory")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-category-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="create-category-description"
                  name="description"
                  value={createCategoryForm.description}
                  onChange={(e) => handleInputChange(e, "createCategory")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-category-order" className="block text-gray-700 text-sm font-bold mb-2">
                  Ordre d'affichage
                </label>
                <input
                  id="create-category-order"
                  type="number"
                  name="order"
                  value={createCategoryForm.order}
                  onChange={(e) => handleInputChange(e, "createCategory")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={createCategoryForm.isActive}
                  onChange={(e) => handleCheckboxChange(e, "createCategory")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="isActive"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Catégorie active
                </label>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateCategoryModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={createCategoryLoading}
                >
                  {createCategoryLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de modification de catégorie */}
      {isEditCategoryModalOpen && selectedCategoryForEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsEditCategoryModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier la catégorie</h2>

            {editCategorySuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Catégorie modifiée avec succès!</p>
              </div>
            )}

            {editCategoryError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{editCategoryError}</p>
              </div>
            )}

            <form onSubmit={handleUpdateCategory}>
              <div className="mb-4">
                <label htmlFor="edit-category-name" className="block text-gray-700 text-sm font-bold mb-2">
                  Nom de la catégorie
                </label>
                <input
                  id="edit-category-name"
                  type="text"
                  name="name"
                  value={editCategoryForm.name}
                  onChange={(e) => handleInputChange(e, "editCategory")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-category-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="edit-category-description"
                  name="description"
                  value={editCategoryForm.description}
                  onChange={(e) => handleInputChange(e, "editCategory")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-category-order" className="block text-gray-700 text-sm font-bold mb-2">
                  Ordre d'affichage
                </label>
                <input
                  id="edit-category-order"
                  type="number"
                  name="order"
                  value={editCategoryForm.order}
                  onChange={(e) => handleInputChange(e, "editCategory")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="editIsActive"
                  name="isActive"
                  checked={editCategoryForm.isActive}
                  onChange={(e) => handleCheckboxChange(e, "editCategory")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="editIsActive"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Catégorie active
                </label>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditCategoryModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={editCategoryLoading}
                >
                  {editCategoryLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de catégorie */}
      {isDeleteCategoryModalOpen && categoryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsDeleteCategoryModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6 flex items-center text-red-600">
              <AlertTriangle className="mr-2" size={24} />
              Supprimer la catégorie
            </h2>

            {deleteCategorySuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Catégorie supprimée avec succès!</p>
              </div>
            )}

            {deleteCategoryError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{deleteCategoryError}</p>
              </div>
            )}

            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer la catégorie{" "}
              <span className="font-semibold">{categoryToDelete.name}</span> ?
            </p>
            <p className="mb-6 text-sm text-red-500">
              Cette action est irréversible et supprimera également tous les
              sujets associés à cette catégorie.
            </p>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setIsDeleteCategoryModalOpen(false)}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                disabled={deleteCategoryLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
                disabled={deleteCategoryLoading}
                onClick={handleDeleteCategory}
              >
                {deleteCategoryLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'affichage des sujets par catégorie */}
      {isViewCategoryTopicsModalOpen && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-5xl relative">
            <button
              onClick={() => setIsViewCategoryTopicsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-2">
              Sujets dans la catégorie : {selectedCategory.name}
            </h2>

            <p className="text-gray-500 mb-6">
              {selectedCategory.description || "Aucune description"}
            </p>

            <div className="flex justify-end mb-4">
              <button
                className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                onClick={() => {
                  setCreateForm((prev) => ({
                    ...prev,
                    categoryId: selectedCategory.id,
                  }));
                  setIsCreateModalOpen(true);
                }}
              >
                <Plus size={18} />
                <span>Créer un sujet dans cette catégorie</span>
              </button>
            </div>

            {topicsLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <p className="ml-2">Chargement des sujets...</p>
              </div>
            ) : topicsError ? (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
                {topicsError}
              </div>
            ) : topics.length === 0 ? (
              <p className="text-center py-8 text-gray-500">
                Aucun sujet disponible dans cette catégorie.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Titre
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Auteur
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Réponses
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Statut
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topics.map((topic) => (
                      <tr key={topic.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {topic.isPinned && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                                Épinglé
                              </span>
                            )}
                            <div className="font-medium text-gray-900 truncate max-w-xs">
                              {topic.title}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {topic.author
                            ? `${topic.author.firstName} ${topic.author.lastName}`
                            : "Utilisateur inconnu"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {topic.createdAt
                            ? new Date(topic.createdAt).toLocaleString(
                                "fr-FR",
                                {
                                  year: "numeric",
                                  month: "numeric",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )
                            : "Date inconnue"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {typeof topic.commentsCount === "number"
                            ? topic.commentsCount
                            : 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {topic.isLocked ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Verrouillé
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Actif
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button
                              className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                              onClick={() => handleEditButtonClick(topic)}
                              title="Éditer"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                              onClick={() => {
                                setTopicToDelete(topic);
                                setIsDeleteModalOpen(true);
                              }}
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                            <button
                              className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100 transition-colors"
                              onClick={() => {
                                console.log("Topic details:", topic);
                                // Vérifier que le contenu est bien présent
                                if (!topic.content && topic.content !== "") {
                                  console.warn(
                                    "Le sujet n'a pas de contenu",
                                    topic
                                  );
                                }

                                const topicWithDefaults = {
                                  ...topic,
                                  id: topic.id,
                                  title: topic.title,
                                  // S'assurer que le contenu n'est jamais null ou undefined
                                  content:
                                    topic.content || "Aucun contenu disponible",
                                  authorId: topic.authorId || "currentUser",
                                  author: topic.author || {
                                    id: "unknown",
                                    firstName: "Utilisateur",
                                    lastName: "Inconnu",
                                  },
                                  createdAt:
                                    topic.createdAt || new Date().toISOString(),
                                  updatedAt:
                                    topic.updatedAt ||
                                    topic.createdAt ||
                                    new Date().toISOString(),
                                  commentsCount: topic.commentsCount || 0,
                                  isLocked: topic.isLocked || false,
                                  isPinned:
                                    topic.isPinned || createForm.isPinned,
                                };
                                console.log(
                                  "Viewing topic with content:",
                                  topicWithDefaults.content
                                );
                                setTopicToView(topicWithDefaults);
                                setIsViewModalOpen(true);
                              }}
                              title="Voir les détails"
                            >
                              <Eye size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setIsViewCategoryTopicsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition de sujet */}
      {isEditModalOpen && selectedTopic && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier le sujet</h2>

            {editSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Sujet modifié avec succès!</p>
              </div>
            )}

            {editError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{editError}</p>
              </div>
            )}

            <form onSubmit={handleUpdateTopic}>
              <div className="mb-4">
                <label htmlFor="edit-topic-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="edit-topic-title"
                  type="text"
                  name="title"
                  value={editForm.title}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-topic-content" className="block text-gray-700 text-sm font-bold mb-2">
                  Contenu
                </label>
                <textarea
                  id="edit-topic-content"
                  name="content"
                  value={editForm.content}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
              </div>

              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="isLocked"
                  name="isLocked"
                  checked={editForm.isLocked}
                  onChange={(e) => handleCheckboxChange(e, "edit")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="isLocked"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Verrouiller le sujet (empêche les nouvelles réponses)
                </label>
              </div>

              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="isPinned"
                  name="isPinned"
                  checked={editForm.isPinned}
                  onChange={(e) => handleCheckboxChange(e, "edit")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="isPinned"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Épingler le sujet (afficher en haut de liste)
                </label>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={editLoading}
                >
                  {editLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de sujet */}
      {isDeleteModalOpen && topicToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6 flex items-center text-red-600">
              <AlertTriangle className="mr-2" size={24} />
              Supprimer le sujet
            </h2>

            {deleteSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Sujet supprimé avec succès!</p>
              </div>
            )}

            {deleteError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{deleteError}</p>
              </div>
            )}

            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer le sujet{" "}
              <span className="font-semibold">{topicToDelete.title}</span> ?
            </p>
            <p className="mb-6 text-sm text-red-500">
              Cette action est irréversible et supprimera définitivement ce
              sujet.
            </p>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                disabled={deleteLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
                disabled={deleteLoading}
                onClick={handleDeleteTopic}
              >
                {deleteLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualisation de sujet */}
      {isViewModalOpen && topicToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-2xl relative">
            <button
              onClick={() => setIsViewModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-2">{topicToView.title}</h2>

            <div className="flex items-center text-sm text-gray-500 mb-4">
              <span>
                Par{" "}
                {topicToView.author
                  ? `${topicToView.author.firstName} ${topicToView.author.lastName}`
                  : "Utilisateur inconnu"}
              </span>
              <span className="mx-2">•</span>
              <span>
                {topicToView.createdAt
                  ? new Date(topicToView.createdAt).toLocaleString("fr-FR", {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Date inconnue"}
              </span>
              {topicToView.isPinned && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Épinglé
                </span>
              )}
              {topicToView.isLocked && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Verrouillé
                </span>
              )}
            </div>

            <div className="mt-4 bg-gray-50 p-4 rounded-md mb-6">
              <p className="whitespace-pre-line">
                {topicToView.content ? topicToView.content : "Aucun contenu"}
              </p>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center text-gray-600 mb-4">
                <MessageSquare size={18} className="mr-2" />
                <h3 className="font-medium">
                  {typeof topicToView.commentsCount === "number"
                    ? topicToView.commentsCount
                    : 0}{" "}
                  réponse
                  {topicToView.commentsCount !== 1 ? "s" : ""}
                </h3>
              </div>

              {/* Ici, on pourrait afficher les réponses si elles sont disponibles */}
              <p className="text-gray-500 italic text-sm">
                Pour voir toutes les réponses, veuillez accéder à la page
                complète du forum.
              </p>
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création de sujet */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Créer un nouveau sujet</h2>

            {createSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Sujet créé avec succès!</p>
              </div>
            )}

            {createError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateTopic}>
              <div className="mb-4">
                <label htmlFor="create-topic-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="create-topic-title"
                  type="text"
                  name="title"
                  value={createForm.title}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-topic-content" className="block text-gray-700 text-sm font-bold mb-2">
                  Contenu
                </label>
                <textarea
                  id="create-topic-content"
                  name="content"
                  value={createForm.content}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
              </div>

              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  id="createIsPinned"
                  name="isPinned"
                  checked={createForm.isPinned}
                  onChange={(e) => handleCheckboxChange(e, "create")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="createIsPinned"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Épingler le sujet (afficher en haut de liste)
                </label>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createLoading}
                >
                  {createLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminForum;
